import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { SanctumAuthService } from '@freesixtyfive/auth';
import {
  TranslateModule,
  TranslateService,
  _,
} from '@ngx-translate/core';
import { getMediaUrl } from '@fsf/util';
import { EnvironmentToken } from '../../../app.config';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { FileUpload, FileUploadModule } from 'primeng/fileupload';
import { FloatLabelModule } from 'primeng/floatlabel';
import { ImageModule } from 'primeng/image';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { PaginatorModule } from 'primeng/paginator';
import { PanelModule } from 'primeng/panel';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { DatePickerModule } from 'primeng/datepicker';
import {
  Defect,
  DefectCheckin,
  DefectComment,
  DefectMedia,
  PsaOption,
  RouteWithSections,
  StoreDefectSchema,
  UpdateDefectSchema,
} from '../../../shared/models/defect.model';
import { DefectService } from '../../../shared/services/defect.service';
import { applyZodErrors } from '../../../shared/util/zod-form-errors';

@Component({
  selector: 'app-defect-detail',
  imports: [
    CommonModule,
    CardModule,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    ToastModule,
    ButtonModule,
    InputNumberModule,
    InputTextModule,
    TextareaModule,
    SelectButtonModule,
    SelectModule,
    FloatLabelModule,
    FileUploadModule,
    ImageModule,
    PaginatorModule,
    PanelModule,
    TableModule,
    TagModule,
    DatePickerModule,
  ],
  providers: [MessageService, DefectService, TranslateService],
  templateUrl: './defect-detail.component.html',
  styleUrl: './defect-detail.component.scss',
})
export class DefectDetailComponent implements OnInit {
  @ViewChild('fileUpload') fileUploadComponent!: FileUpload;

  #fb = inject(FormBuilder);
  #route = inject(ActivatedRoute);
  #router = inject(Router);
  #messageService = inject(MessageService);
  #translateService = inject(TranslateService);
  #authService = inject(SanctumAuthService);
  #apiUrl = inject(EnvironmentToken).apiUrl;
  defectService = inject(DefectService);

  prefilledModelType = input<string | null>(null);
  prefilledModelId = input<string | null>(null);

  defect = signal<Defect | null>(null);
  isEditMode = signal(false);
  newCommentText = signal('');
  commentFirst = signal(0);
  commentsPerPage = 10;
  #returnUrl = '/defects';

  sortedComments = computed(() => {
    const comments = this.defect()?.comments ?? [];
    return [...comments].sort((a, b) => {
      const dateA = a.datetime ? new Date(a.datetime).getTime() : 0;
      const dateB = b.datetime ? new Date(b.datetime).getTime() : 0;
      return dateB - dateA;
    });
  });

  paginatedComments = computed(() => {
    const all = this.sortedComments();
    return all.slice(
      this.commentFirst(),
      this.commentFirst() + this.commentsPerPage,
    );
  });

  form: FormGroup;

  modelTypeOptions = ['route_section', 'psa'].map((key) => ({
    key,
    value: this.#translateService.instant(`defect.types.${key}`),
  }));

  statusOptions = (() => {
    const keys = ['new', 'in_progress', 'work_done'];
    if (this.#authService.permissions().includes('approve:defects')) {
      keys.push('accepted');
    }
    return keys.map((key) => ({
      key,
      value: this.#translateService.instant(`defect.statuses.${key}`),
    }));
  })();

  #sectionTypeMap: Record<string, string> = {
    element: 'inspectionItem.sectionTypeElement',
    platform: 'inspectionItem.sectionTypePlatform',
  };

  routeSectionOptions = computed(() => {
    const routes = this.defectService.routeOptions();
    return routes.map((route: RouteWithSections) => ({
      label: route.name,
      items: route.sections.map((section) => ({
        label: section.type
          ? `${section.name} (${this.#translateService.instant(this.#sectionTypeMap[section.type] ?? section.type)})`
          : section.name,
        value: section.id,
      })),
    }));
  });

  psaOptions = computed(() => {
    return this.defectService.psaOptions().map((psa: PsaOption) => ({
      label: psa.name,
      value: psa.id,
    }));
  });

  priorityOptions = ['low', 'medium', 'high', 'critical'].map((key) => ({
    key,
    value: this.#translateService.instant(`defect.priorities.${key}`),
  }));

  repairTimingOptions = ['during_hours', 'outside_hours', 'any'].map(
    (key) => ({
      key,
      value: this.#translateService.instant(`defect.repair_timing.${key}`),
    }),
  );

  responsibleUserOptions = computed(() => {
    return this.defectService.responsibleUserOptions().map((u) => ({
      label: u.name,
      value: u.id,
    }));
  });

  sortedCheckins = computed(() => {
    const checkins = this.defect()?.checkins ?? [];
    return [...checkins].sort(
      (a, b) =>
        new Date(b.checked_in_at).getTime() -
        new Date(a.checked_in_at).getTime(),
    );
  });

  totalCheckinMinutes = computed(() => {
    return this.sortedCheckins().reduce(
      (sum, c) => sum + this.checkinDurationMinutes(c),
      0,
    );
  });

  checkinDurationMinutes(checkin: DefectCheckin): number {
    const start = new Date(checkin.checked_in_at).getTime();
    const end = checkin.checked_out_at
      ? new Date(checkin.checked_out_at).getTime()
      : Date.now();
    return Math.max(0, Math.round((end - start) / 60000));
  }

  formatDuration(minutes: number): string {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h === 0) return `${m}min`;
    return m > 0 ? `${h}h ${m}min` : `${h}h`;
  }

  constructor() {
    this.form = this.#fb.group({
      title: [''],
      description: [''],
      modelType: ['route_section'],
      modelId: [null],
      status: ['new'],
      priority: [null],
      dueDate: [null],
      estimatedRepairTime: [null as number | null],
      repairTiming: [null],
      responsibleUserId: [null],
    });

    effect(() => {
      const prefillType = this.prefilledModelType();
      const prefillId = this.prefilledModelId();
      if (prefillType) {
        this.form.patchValue({ modelType: prefillType });
      }
      if (prefillId) {
        this.form.patchValue({ modelId: prefillId });
      }
    });
  }

  ngOnInit(): void {
    const id = this.#route.snapshot.paramMap.get('id');

    const queryModelType = this.#route.snapshot.queryParamMap.get('modelType');
    const queryModelId = this.#route.snapshot.queryParamMap.get('modelId');
    const returnUrl = this.#route.snapshot.queryParamMap.get('returnUrl');
    if (queryModelType) {
      this.form.patchValue({ modelType: queryModelType });
    }
    if (queryModelId) {
      this.form.patchValue({ modelId: queryModelId });
    }
    if (returnUrl) {
      this.#returnUrl = returnUrl;
    }

    this.defectService.getRouteOptions();
    this.defectService.getPsaOptions();
    this.defectService.getResponsibleUsers();

    if (id && id !== 'new') {
      this.isEditMode.set(true);
      this.defectService.getDefect(Number(id)).subscribe({
        next: (response: any) => {
          const defectData = response.data ?? response;
          this.defect.set(defectData);
          this.form.patchValue({
            title: defectData.title,
            description: defectData.description,
            modelType: defectData.model_type,
            modelId: defectData.model_id,
            status: defectData.status,
            priority: defectData.priority,
            dueDate: defectData.due_date
              ? new Date(defectData.due_date)
              : null,
            estimatedRepairTime:
              defectData.estimated_repair_time ?? null,
            repairTiming: defectData.repair_timing,
            responsibleUserId:
              defectData.responsible_user?.id ?? null,
          });
        },
        error: () => {
          this.#messageService.add({
            severity: 'error',
            summary: this.#translateService.instant('label.errorHeader'),
            detail: this.#translateService.instant('defect.loadError'),
          });
        },
      });
    }
  }

  onModelTypeChange() {
    this.form.patchValue({ modelId: null });
  }

  isInvalid(controlName: string): boolean {
    const control = this.form.get(controlName);
    return !!(control?.invalid && control.touched);
  }

  getErrorKey(controlName: string): string {
    const control = this.form.get(controlName);
    if (control?.errors?.['zod']) {
      return control.errors['zod'].message;
    }
    if (control?.errors?.['required']) {
      return 'validation.required';
    }
    if (control?.errors?.['maxlength']) {
      return 'validation.maxLength';
    }
    return 'validation.invalid';
  }

  onSubmit() {
    const dueDateValue = this.form.value.dueDate;
    const dueDate = dueDateValue instanceof Date
      ? dueDateValue.toISOString().split('T')[0]
      : dueDateValue || null;
    const responsibleUserId = this.form.value.responsibleUserId
      ? Number(this.form.value.responsibleUserId)
      : null;

    const rawData = {
      title: this.form.value.title,
      description: this.form.value.description,
      model_type: this.form.value.modelType,
      model_id: String(this.form.value.modelId ?? ''),
      status: this.form.value.status,
      priority: this.form.value.priority || null,
      due_date: dueDate,
      estimated_repair_time:
        this.form.value.estimatedRepairTime != null
          ? Number(this.form.value.estimatedRepairTime)
          : null,
      repair_timing: this.form.value.repairTiming || null,
      responsible_user_id: responsibleUserId,
      ...(!this.isEditMode() && {
        reporting_time: new Date().toISOString().split('T')[0],
      }),
    };

    const schema = this.isEditMode() ? UpdateDefectSchema : StoreDefectSchema;
    const result = schema.safeParse(rawData);
    if (!result.success) {
      applyZodErrors(result.error, this.form, {
        model_type: 'modelType',
        model_id: 'modelId',
      });
      return;
    }

    const formData = new FormData();
    formData.append('title', this.form.value.title);
    formData.append('description', this.form.value.description);
    formData.append('model_type', this.form.value.modelType);
    formData.append('model_id', this.form.value.modelId);
    formData.append('status', this.form.value.status);

    if (this.form.value.priority) {
      formData.append('priority', this.form.value.priority);
    }
    if (dueDate) {
      formData.append('due_date', dueDate);
    }
    if (this.form.value.estimatedRepairTime != null) {
      formData.append(
        'estimated_repair_time',
        String(this.form.value.estimatedRepairTime),
      );
    }
    if (this.form.value.repairTiming) {
      formData.append('repair_timing', this.form.value.repairTiming);
    }
    if (responsibleUserId) {
      formData.append(
        'responsible_user_id',
        String(responsibleUserId),
      );
    }

    if (!this.isEditMode()) {
      formData.append('reporting_time', new Date().toISOString().split('T')[0]);
    }

    if (this.fileUploadComponent?.files?.length) {
      for (const file of this.fileUploadComponent.files) {
        formData.append('files[]', file);
      }
    }

    if (this.isEditMode()) {
      const defect = this.defect();
      if (!defect) return;

      formData.append('_method', 'PUT');

      this.defectService.updateDefect(formData, defect.id).subscribe({
        next: () => {
          this.#messageService.add({
            severity: 'success',
            summary: this.#translateService.instant('label.successHeader'),
            detail: this.#translateService.instant('defect.updated'),
          });
          this.#router.navigate([this.#returnUrl]);
        },
        error: () => {
          this.#messageService.add({
            severity: 'error',
            summary: this.#translateService.instant('label.errorHeader'),
            detail: this.#translateService.instant('defect.updateError'),
          });
        },
      });
    } else {
      if (this.newCommentText().trim()) {
        formData.append('comments[0][comment]', this.newCommentText().trim());
        formData.append('comments[0][datetime]', new Date().toISOString());
      }

      this.defectService.createDefect(formData).subscribe({
        next: () => {
          this.#messageService.add({
            severity: 'success',
            summary: this.#translateService.instant('label.successHeader'),
            detail: this.#translateService.instant('defect.created'),
          });
          this.#router.navigate([this.#returnUrl]);
        },
        error: () => {
          this.#messageService.add({
            severity: 'error',
            summary: this.#translateService.instant('label.errorHeader'),
            detail: this.#translateService.instant('defect.createError'),
          });
        },
      });
    }
  }

  canDeleteComment(comment: DefectComment): boolean {
    return true;

    //TODO: implement getUser into offical auth package

    // const user = this.#authService.getUser();
    // if (!user) return false;
    // const isAuthor = String(user.id) === String(comment.user?.id);
    // const isAdmin = user.roles?.some(
    //   (r: any) => r === 'admin' || r.name === 'admin',
    // );
    // return isAuthor || isAdmin;
  }

  onCommentPageChange(event: any) {
    this.commentFirst.set(event.first);
  }

  submitComment() {
    const defect = this.defect();
    const text = this.newCommentText().trim();
    if (!defect || !text) return;

    this.defectService.addComment(defect.id, text).subscribe({
      next: (response: any) => {
        const defectData = response.data ?? response;
        this.defect.set(defectData);
        this.newCommentText.set('');
        this.commentFirst.set(0);
      },
      error: () => {
        this.#messageService.add({
          severity: 'error',
          summary: this.#translateService.instant('label.errorHeader'),
          detail: this.#translateService.instant('defect.commentAddError'),
        });
      },
    });
  }

  removeComment(comment: DefectComment) {
    const defect = this.defect();
    if (!defect) return;

    this.defectService.deleteComment(defect.id, comment.id).subscribe({
      next: () => {
        this.defect.update((d) => {
          if (!d) return d;
          return {
            ...d,
            comments: d.comments.filter((c) => c.id !== comment.id),
          };
        });
      },
      error: () => {
        this.#messageService.add({
          severity: 'error',
          summary: this.#translateService.instant('label.errorHeader'),
          detail: this.#translateService.instant('defect.commentDeleteError'),
        });
      },
    });
  }

  deleteMedia(media: DefectMedia) {
    const defect = this.defect();
    if (!defect) return;

    this.defectService.deleteMedia(defect.id, media.id).subscribe({
      next: () => {
        this.defect.update((d) => {
          if (!d) return d;
          return {
            ...d,
            media: d.media.filter((m) => m.id !== media.id),
          };
        });
      },
      error: () => {
        this.#messageService.add({
          severity: 'error',
          summary: this.#translateService.instant('label.errorHeader'),
          detail: this.#translateService.instant('defect.mediaDeleteError'),
        });
      },
    });
  }

  getMediaUrl(url: string): string {
    return getMediaUrl(url, this.#apiUrl);
  }

  navigateBack() {
    this.#router.navigate([this.#returnUrl]);
  }
}

_([
  'defect.priorities.low',
  'defect.priorities.medium',
  'defect.priorities.high',
  'defect.priorities.critical',
  'defect.repair_timing.during_hours',
  'defect.repair_timing.outside_hours',
  'defect.repair_timing.any',
]);
